import { Component } from "react";
import { createRoot } from "react-dom/client";
import PopupComponent from "../component";
import data from "../../data.json";
import { SHARE_COPY_RESET_MS, normalizeVideoFxs } from "../constants";
import {
  generateNewMood,
  getMoodByName,
  moodSpeedUpMode,
  moodToPopupState,
  popupStateToMood,
} from "../shuffle-moods";
import { PREVIEW_TOOL_URL, resolveUpdateBannerContent } from "../update-banner";
import { hostPageUrl, resolveStudioLink } from "../studio-link";

// What the footer's Reset returns the controls to: everything off, normal
// speed. Mirrors the controls in the initial state below.
const CONTROL_DEFAULTS = {
  slowControl: 1,
  reverbAmount: 0,
  videoFx: null,
  lowPassMode: false,
  lowPassCutoff: 1000,
  highPassMode: false,
  highPassCutoff: 500,
  bitcrusherMode: false,
  bitcrusherAmount: 0.45,
  bassBoostMode: false,
  bassBoostAmount: 0.5,
  phaserMode: false,
  phaserAmount: 0.5,
  echoMode: false,
  echoAmount: 0.5,
  lofiTapeMode: false,
  lofiHissVolume: 1,
  rainyMode: false,
  rainVolume: 0.6,
  coffeeShopMode: false,
  coffeeShopVolume: 0.6,
  seashoreMode: false,
  seashoreVolume: 0.6,
  berghainMode: false,
  berghainVolume: 0.6,
  hotelLoungeMode: false,
  hotelLoungeVolume: 0.6,
  cocktailBarMode: false,
  cocktailBarVolume: 0.6,
  vinylCrackleMode: false,
  vinylCrackleVolume: 0.55,
  preservePitchMode: false,
  speedUpMode: false,
};

class PopupDemoComponent extends Component {
  constructor(props) {
    super(props);
    this.preMoodSnapshot = null;
    this.state = {
      slowControl: 1,
      reverbAmount: 0,
      videoFx: null,
      videoFxs: normalizeVideoFxs(data.videoFxs),
      lowPassMode: false,
      lowPassCutoff: 1000,
      highPassMode: false,
      highPassCutoff: 500,
      bitcrusherMode: false,
      bitcrusherAmount: 0.45,
      bassBoostMode: false,
      bassBoostAmount: 0.5,
      phaserMode: false,
      phaserAmount: 0.5,
      echoMode: false,
      echoAmount: 0.5,
      lofiTapeMode: false,
      lofiHissVolume: 1,
      rainyMode: false,
      rainVolume: 0.6,
      coffeeShopMode: false,
      coffeeShopVolume: 0.6,
      seashoreMode: false,
      seashoreVolume: 0.6,
      berghainMode: false,
      berghainVolume: 0.6,
      hotelLoungeMode: false,
      hotelLoungeVolume: 0.6,
      cocktailBarMode: false,
      cocktailBarVolume: 0.6,
      vinylCrackleMode: false,
      vinylCrackleVolume: 0.55,
      preservePitchMode: false,
      speedUpMode: false,
      skin: props.skin ?? "color",
      shareMediaOptIn: false,
      trackName: "Demo Track",
      url: "https://www.youtube.com/watch?v=demo",
      sharingStatus: null,
      isSlowedEnabled: true,
      isFeedbackEnabled: true,
      isRateAppEnabled: true,
      ratePromptVisible: !!props.ratePrompt,
      updateBannerVisible: !!props.updateBanner,
      // A host page (Slowed Studio) opens the panel on its own settings.
      ...props.initialState,
    };
  }

  // A host page that plays the sound itself (Slowed Studio) follows the
  // panel through this; the demo pages pass nothing.
  componentDidUpdate(prevProps, prevState) {
    if (prevState !== this.state) this.props.onStateChange?.(this.state);
  }

  resetControls() {
    this.preMoodSnapshot = null;
    this.setState({ ...CONTROL_DEFAULTS, isSlowedEnabled: true });
  }

  componentWillUnmount() {
    clearTimeout(this.shareCopyResetTimer);
  }

  scheduleShareCopyReset() {
    clearTimeout(this.shareCopyResetTimer);
    this.shareCopyResetTimer = setTimeout(() => {
      this.setState({ sharingStatus: null });
    }, SHARE_COPY_RESET_MS);
  }

  render() {
    // The demo speaks for the release it was built from.
    const updateBannerContent = resolveUpdateBannerContent({
      version: process.env.VERSION,
      toolUrl: PREVIEW_TOOL_URL,
    });
    const {
      isHeaderVisible,
      isPro,
      isProFeaturesLocked,
      trialMode,
      trialEnded,
      remainingTrialDays,
      usageAvailable,
      usageHit,
      usageRemaining,
      usagePeriod,
      usageProgress,
      usagePromo,
      weeklyCapCount,
      testMode,
      initialSheet,
      onSheetChange,
      onUpgradeClick,
      onUsageClick,
      onAboutClick,
      onFeedbackClick,
      onHelpClick,
      onChangelogClick,
      onRateAppClick,
      onCustomizeShortcut,
      onSkinChanged,
      hideManagePlan,
      notSupported,
      accessSite,
      hasVideo,
      hasEffects,
      spotifyBeta,
      onManageSubscriptionClick,
      onOpenYouTubeClick,
      onEnableSiteClick,
    } = this.props;
    const {
      isSlowedEnabled,
      slowControl,
      reverbAmount,
      lowPassMode,
      lowPassCutoff,
      highPassMode,
      highPassCutoff,
      bitcrusherMode,
      bitcrusherAmount,
      bassBoostMode,
      bassBoostAmount,
      phaserMode,
      phaserAmount,
      echoMode,
      echoAmount,
      lofiTapeMode,
      lofiHissVolume,
      rainyMode,
      rainVolume,
      coffeeShopMode,
      coffeeShopVolume,
      seashoreMode,
      seashoreVolume,
      berghainMode,
      berghainVolume,
      hotelLoungeMode,
      hotelLoungeVolume,
      cocktailBarMode,
      cocktailBarVolume,
      vinylCrackleMode,
      vinylCrackleVolume,
      preservePitchMode,
      speedUpMode,
      skin,
      videoFxs,
      videoFx,
      isFeedbackEnabled,
      isRateAppEnabled,
      shareMediaOptIn,
      trackName,
      url,
      sharingStatus,
    } = this.state;

    return (
      <PopupComponent
        isHeaderVisible={isHeaderVisible || true}
        isSlowedEnabled={isSlowedEnabled}
        isFeedbackEnabled={isFeedbackEnabled}
        isRateAppEnabled={isRateAppEnabled}
        isPro={isPro}
        isProFeaturesLocked={isProFeaturesLocked}
        hideManagePlan={hideManagePlan}
        trialMode={trialMode}
        trialEnded={trialEnded}
        remainingTrialDays={remainingTrialDays}
        usageAvailable={usageAvailable}
        usageHit={usageHit}
        usageRemaining={usageRemaining}
        usagePeriod={usagePeriod}
        usageProgress={usageProgress}
        usagePromo={usagePromo}
        weeklyCapCount={weeklyCapCount}
        testMode={testMode}
        initialSheet={initialSheet}
        onSheetChange={onSheetChange}
        hiddenActions={this.props.hiddenActions}
        onResetControls={() => this.resetControls()}
        slowControl={slowControl}
        reverbAmount={reverbAmount}
        videoFx={videoFx}
        videoFxs={videoFxs}
        lowPassMode={lowPassMode}
        lowPassCutoff={lowPassCutoff}
        highPassMode={highPassMode}
        highPassCutoff={highPassCutoff}
        bitcrusherMode={bitcrusherMode}
        bitcrusherAmount={bitcrusherAmount}
        bassBoostMode={bassBoostMode}
        bassBoostAmount={bassBoostAmount}
        phaserMode={phaserMode}
        phaserAmount={phaserAmount}
        echoMode={echoMode}
        echoAmount={echoAmount}
        lofiTapeMode={lofiTapeMode}
        lofiHissVolume={lofiHissVolume}
        rainyMode={rainyMode}
        rainVolume={rainVolume}
        coffeeShopMode={coffeeShopMode}
        coffeeShopVolume={coffeeShopVolume}
        seashoreMode={seashoreMode}
        seashoreVolume={seashoreVolume}
        berghainMode={berghainMode}
        berghainVolume={berghainVolume}
        hotelLoungeMode={hotelLoungeMode}
        hotelLoungeVolume={hotelLoungeVolume}
        cocktailBarMode={cocktailBarMode}
        cocktailBarVolume={cocktailBarVolume}
        vinylCrackleMode={vinylCrackleMode}
        vinylCrackleVolume={vinylCrackleVolume}
        preservePitchMode={preservePitchMode}
        speedUpMode={speedUpMode}
        skin={skin}
        shareMediaOptIn={shareMediaOptIn}
        trackName={trackName}
        url={url}
        sharingStatus={sharingStatus}
        onUpgradeClick={onUpgradeClick}
        onUsageClick={onUsageClick}
        notSupported={notSupported}
        accessSite={accessSite}
        hasVideo={hasVideo}
        hasEffects={hasEffects}
        spotifyBeta={spotifyBeta}
        onManageSubscriptionClick={onManageSubscriptionClick}
        onOpenYouTubeClick={onOpenYouTubeClick}
        onEnableSiteClick={onEnableSiteClick}
        onAboutClick={onAboutClick}
        onFeedbackClick={onFeedbackClick}
        onChangelogClick={onChangelogClick}
        onHelpClick={onHelpClick}
        ratePromptVisible={this.state.ratePromptVisible}
        onRatePromptClick={() => {
          this.setState({ ratePromptVisible: false });
          this.props.onRatePromptClick?.();
        }}
        onRatePromptDismiss={() => {
          this.setState({ ratePromptVisible: false });
          this.props.onRatePromptDismiss?.();
        }}
        updateBannerVisible={this.state.updateBannerVisible}
        updateBannerText={updateBannerContent?.text}
        updateBannerCta={updateBannerContent?.cta}
        toolUrl={this.props.toolUrl}
        onOpenToolClick={this.props.onOpenToolClick}
        studioUrl={resolveStudioLink({
          toolUrl: PREVIEW_TOOL_URL,
          pageUrl: hostPageUrl(),
          medium: "web-panel",
          version: process.env.VERSION,
        })}
        onStudioClick={this.props.onStudioClick}
        onUpdateBannerClick={() => {
          this.setState({ updateBannerVisible: false });
          this.props.onUpdateBannerClick?.();
        }}
        onUpdateBannerDismiss={() => {
          this.setState({ updateBannerVisible: false });
          this.props.onUpdateBannerDismiss?.();
        }}
        onRateAppClick={onRateAppClick}
        onCustomizeShortcut={onCustomizeShortcut}
        onSlowedToggle={(value) => {
          this.setState({ isSlowedEnabled: value });
        }}
        onSlowControlChange={(value) => {
          this.setState({ slowControl: value, isSlowedEnabled: true });
        }}
        onVideoFxChange={(value) => {
          this.setState({ videoFx: value, isSlowedEnabled: true });
        }}
        onLowPassModeChange={(value) => {
          this.setState({ lowPassMode: value });
        }}
        onLowPassCutoffChange={(value) => {
          this.setState({ lowPassCutoff: value });
        }}
        onHighPassModeChange={(value) => {
          this.setState({ highPassMode: value });
        }}
        onHighPassCutoffChange={(value) => {
          this.setState({ highPassCutoff: value });
        }}
        onBitcrusherModeChange={(value) => {
          this.setState({ bitcrusherMode: value });
        }}
        onBitcrusherAmountChange={(value) => {
          this.setState({ bitcrusherAmount: value });
        }}
        onBassBoostModeChange={(value) => {
          this.setState({ bassBoostMode: value });
        }}
        onBassBoostAmountChange={(value) => {
          this.setState({ bassBoostAmount: value });
        }}
        onPhaserModeChange={(value) => {
          this.setState({ phaserMode: value });
        }}
        onPhaserAmountChange={(value) => {
          this.setState({ phaserAmount: value });
        }}
        onEchoModeChange={(value) => {
          this.setState({ echoMode: value });
        }}
        onEchoAmountChange={(value) => {
          this.setState({ echoAmount: value });
        }}
        onLofiTapeModeChange={(value) => {
          this.setState({ lofiTapeMode: value });
        }}
        onLofiHissVolumeChange={(value) => {
          this.setState({ lofiHissVolume: value });
        }}
        onRainyModeChange={(value) => {
          this.setState({
            rainyMode: value,
          });
        }}
        onRainVolumeChange={(value) => {
          this.setState({ rainVolume: value });
        }}
        onCoffeeShopModeChange={(value) => {
          this.setState({
            coffeeShopMode: value,
          });
        }}
        onCoffeeShopVolumeChange={(value) => {
          this.setState({ coffeeShopVolume: value });
        }}
        onSeashoreModeChange={(value) => {
          this.setState({
            seashoreMode: value,
          });
        }}
        onSeashoreVolumeChange={(value) => {
          this.setState({ seashoreVolume: value });
        }}
        onBerghainModeChange={(value) => {
          this.setState({
            berghainMode: value,
          });
        }}
        onBerghainVolumeChange={(value) => {
          this.setState({ berghainVolume: value });
        }}
        onHotelLoungeModeChange={(value) => {
          this.setState({
            hotelLoungeMode: value,
          });
        }}
        onHotelLoungeVolumeChange={(value) => {
          this.setState({ hotelLoungeVolume: value });
        }}
        onCocktailBarModeChange={(value) => {
          this.setState({
            cocktailBarMode: value,
          });
        }}
        onCocktailBarVolumeChange={(value) => {
          this.setState({ cocktailBarVolume: value });
        }}
        onVinylCrackleModeChange={(value) => {
          this.setState({ vinylCrackleMode: value });
        }}
        onVinylCrackleVolumeChange={(value) => {
          this.setState({ vinylCrackleVolume: value });
        }}
        onPreservePitchModeChange={(value) => {
          this.setState({ preservePitchMode: value });
        }}
        onSpeedUpModeChange={(value) => {
          this.setState({
            speedUpMode: value,
            ...(!value ? { slowControl: 1, isSlowedEnabled: true } : {}),
          });
        }}
        onAIShuffle={() => {
          if (!this.preMoodSnapshot) {
            this.preMoodSnapshot = popupStateToMood(this.state);
          }
          const mood = generateNewMood();
          this.setState(moodToPopupState(mood));
          return Promise.resolve(mood);
        }}
        onApplyMood={(moodOrName) => {
          const mood =
            typeof moodOrName === "string"
              ? getMoodByName(moodOrName)
              : moodOrName;
          if (!mood) return Promise.resolve(null);
          if (!this.preMoodSnapshot) {
            this.preMoodSnapshot = {
              ...popupStateToMood(this.state),
              speedUpMode: !!this.state.speedUpMode,
            };
          }
          this.setState({
            ...moodToPopupState(mood),
            speedUpMode: moodSpeedUpMode(mood, this.state.speedUpMode),
          });
          return Promise.resolve(mood);
        }}
        onClearMood={() => {
          const snapshot = this.preMoodSnapshot;
          if (!snapshot) return Promise.resolve(null);
          this.preMoodSnapshot = null;
          this.setState({
            ...moodToPopupState(snapshot),
            speedUpMode: moodSpeedUpMode(snapshot, this.state.speedUpMode),
          });
          return Promise.resolve(snapshot);
        }}
        onSkinChange={(value) => {
          this.setState({ skin: value });
          onSkinChanged?.(value);
        }}
        onReverbAmountChange={(value) => {
          this.setState({ reverbAmount: value, isSlowedEnabled: true });
        }}
        onShareMediaOptInChange={(value) => {
          this.setState({ shareMediaOptIn: value });
        }}
        onCopyShareUrl={(value) => {
          this.setState({
            sharingStatus: { type: value, value: "copied" },
          });
          if (value === "copy") this.scheduleShareCopyReset();
        }}
      />
    );
  }
}

self.Slowed.Components.PopupDemo = PopupDemoComponent;

self.Slowed.mountPopupDemo = (element, props) => {
  element._slowedRoot = element._slowedRoot || createRoot(element);
  element._slowedRoot.render(<PopupDemoComponent {...props} />);
};

export default PopupDemoComponent;
